import { BadgeCheck, Star } from "lucide-react";
import { useT } from "@/lib/i18n";

export const VerifiedBadge = ({ date, testid = "verified-badge" }) => {
  const { t, fmtDate } = useT();
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-800" data-testid={testid}>
      <BadgeCheck className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2.4} />
      {date ? t("verified_on", { date: fmtDate(date) }) : t("verified")}
    </span>
  );
};

export const Stars = ({ value = 0, count, size = "h-4 w-4", testid }) => (
  <span className="inline-flex items-center gap-1" data-testid={testid}>
    <span className="inline-flex">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`${size} ${i <= Math.round(value) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
      ))}
    </span>
    {count !== undefined && <span className="text-xs font-semibold text-slate-500">{value ? value.toFixed(1) : "–"} ({count})</span>}
  </span>
);

export const StarInput = ({ value, onChange }) => (
  <div className="flex gap-1" data-testid="star-input">
    {[1, 2, 3, 4, 5].map((i) => (
      <button key={i} type="button" onClick={() => onChange(i)} data-testid={`star-input-${i}`} className="p-1 transition-transform active:scale-90">
        <Star className={`h-9 w-9 ${i <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
      </button>
    ))}
  </div>
);

const STATUS_STYLE = {
  pending: "bg-amber-50 text-amber-800 border-amber-300",
  verified: "bg-emerald-50 text-emerald-800 border-emerald-300",
  suspended: "bg-slate-100 text-slate-700 border-slate-300",
  removed: "bg-red-50 text-red-800 border-red-300",
  open: "bg-amber-50 text-amber-800 border-amber-300",
  confirmed: "bg-red-50 text-red-800 border-red-300",
  dismissed: "bg-slate-100 text-slate-700 border-slate-300",
};

export const StatusBadge = ({ status, kind = "st", testid }) => {
  const { t } = useT();
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-bold ${STATUS_STYLE[status] || ""}`} data-testid={testid || `status-${status}`}>
      {t(`${kind}_${status}`)}
    </span>
  );
};

export const ReplyNote = ({ reply }) => {
  const { t } = useT();
  if (!reply?.text) return null;
  return <p className="mt-2 rounded-lg border-l-4 border-saffron bg-saffron-50 px-3 py-2 text-xs text-slate-700" data-testid="review-reply"><b>{t("owner_reply")}:</b> {reply.text}</p>;
};

export const Empty = ({ children, testid }) => (
  <div className="rounded-2xl border border-dashed border-line bg-white/60 px-5 py-10 text-center text-sm text-slate-500" data-testid={testid}>
    {children}
  </div>
);

export const Loading = () => {
  const { t } = useT();
  return <div className="px-5 py-16 text-center text-sm text-slate-400" data-testid="loading">{t("loading")}</div>;
};

export const LoadMore = ({ p, testid = "load-more-btn" }) => {
  const { t } = useT();
  if (!p.hasMore) return null;
  return (
    <div className="flex justify-center pt-2">
      <button onClick={p.more} disabled={p.loadingMore} data-testid={testid}
        className="rounded-full border border-line bg-white px-6 py-2.5 text-sm font-bold text-ink transition-colors duration-200 hover:border-saffron hover:text-saffron disabled:opacity-50">
        {p.loadingMore ? t("loading") : t("load_more", { n: p.total - p.items.length })}
      </button>
    </div>
  );
};


export const PageTitle = ({ title, sub }) => (
  <div className="px-5 pb-2 pt-6">
    <h1 className="font-display text-3xl font-extrabold tracking-tight text-ink" data-testid="page-title">{title}</h1>
    {sub && <p className="mt-1 text-sm text-slate-500">{sub}</p>}
  </div>
);
